import "./style.scss";
import * as React from "react";
import { Table, Icon, Popover } from "antd";
import { ProgressBar } from "../../../../core/Progress";
import { WarningBar } from "../../../../common/components/WarningBar";
import { sendGAEvent } from "../../../../common/google_analytics";
import { ColumnTitle } from "./ColumnTitle";

export class AnalyticsBudgetTable extends React.Component<any, {}> {
  mapDataToMainTableRows() {
    if (this.props.data) {
      let aggregateValues = this.props.data.map((entry: any) => {
        let spendToDate = entry.flyer_runs.reduce((acc: number, curr: any) => {
          return acc + curr.spend;
        }, 0);
        let totalBillableEngagements = entry.flyer_runs.reduce(
          (acc: number, curr: any) => {
            return acc + curr.billable_engagements;
          },
          0
        );
        return { spendToDate, totalBillableEngagements };
      });
      let rowObject = this.props.data.map((entry: any, index: number) => {
        return {
          key: `${entry.budget_id}_budget_row_${index}`,
          spend_to_date: (
            <div className="spendToDate pure-u-g">
              <span className="pure-u-10-24">
                {aggregateValues[index].spendToDate
                  ? `$${formatNumber(aggregateValues[index].spendToDate)}`
                  : "$0"}
              </span>
              <ProgressBar
                className="spendToDateProgressBar pure-u-14-24"
                strokeWidth={5}
                percent={
                  aggregateValues[index].spendToDate > entry.quota
                    ? 100
                    : Math.floor(
                        (100 * aggregateValues[index].spendToDate) / entry.quota
                      )
                }
              />
            </div>
          ),
          spend_to_date_val: aggregateValues[index].spendToDate,
          quota: entry.quota ? `$${formatNumber(entry.quota)}` : "$0",
          quota_val: entry.quota,
          billable_engagements: aggregateValues[index].totalBillableEngagements
            ? formatNumber(aggregateValues[index].totalBillableEngagements)
            : 0,
          billable_engagements_val:
            aggregateValues[index].totalBillableEngagements,
          campaign_name: `${entry.campaign.name} - ${entry.name}`,
          flyer_runs: entry.flyer_runs
        };
      });
      return rowObject;
    }
  }

  toggleExpand = (expanded: boolean, record: any) => {
    if (expanded) this.fireGABeacon();
  };

  fireGABeacon = () => {
    const { gaUserContext, merchantName } = this.props;
    sendGAEvent("Finance Tab", "Expand Row", merchantName, gaUserContext);
  };

  render() {
    const billableEvTitle = (
      <ColumnTitle
        title="Total Billable Engagements"
        hoverContent={billableEvHoverContent}
      />
    );
    const spendTitle = (
      <ColumnTitle title="Spend to Date" hoverContent={spendHoverContent} />
    );

    let columns = [
      {
        title: "Campaign - Budget",
        dataIndex: "campaign_name",
        key: "campaign_name",
        sorter: sortName,
        width: 200
      },
      {
        title: spendTitle,
        dataIndex: "spend_to_date",
        key: "spend_to_date",
        sorter: (a: any, b: any) => {
          return a.spend_to_date_val - b.spend_to_date_val;
        },
        width: 220
      },
      {
        title: billableEvTitle,
        dataIndex: "billable_engagements",
        key: "total_billable_engagements",
        sorter: (a: any, b: any) => {
          return a.billable_engagements_val - b.billable_engagements_val;
        },
        width: 140
      },
      {
        title: "Total Budget Amount",
        dataIndex: "quota",
        key: "quota",
        sorter: (a: any, b: any) => {
          return a.quota_val - b.quota_val;
        },
        width: 110
      }
    ];

    return (
      <div className="AnalyticsBudgetTable">
        <WarningBar icon="exclamation">
          This table shows information for budgets associated to the flyers you
          have selected.
        </WarningBar>
        <Table
          size="middle"
          className="analytics-budget-table"
          columns={columns}
          dataSource={this.mapDataToMainTableRows()}
          expandedRowRender={expandedRowRender}
          pagination={false}
          expandIconAsCell={true}
          onExpand={this.toggleExpand}
        />
      </div>
    );
  }
}

const spendHoverContent = (
  <p>
    Totals provided here do not include the applicable taxes, if any, and <br />
    may differ from your invoices as a result of billing adjustments.
  </p>
);

const billableEvHoverContent = (
  <p>
    An action taken by end-user to read a Digital Publication that has been
    promoted on any of Flipp's platforms. <br />
    End-user's action will be considered a read when the end-user: a) opens,
    retrieves, downloads or <br />
    otherwise takes an action to view the Digital Publication; and b) views the
    Digital Publication for at least six (6) <br />
    seconds, or performs an interactive action subsequent to (a) such as viewing
    a video, clicking through <br />
    to a store, and panning, zooming or using a navigation interface.
  </p>
);

const roundToHundreds = (value: number) => Math.round(value * 100) / 100;

const formatNumber = (value: number) => roundToHundreds(value).toLocaleString();

const expandedRowRender = (entry: any) => {
  const columns = [
    {
      title: "Flyer Name",
      dataIndex: "flyer_run_name",
      key: "flyer_run_name",
      width: 275
    },
    {
      title: "Spend to Date",
      dataIndex: "spend_to_date",
      key: "spend_to_date",
      width: 301
    },
    {
      title: "Billable Engagements",
      dataIndex: "billable_engagements",
      key: "billable_engagements"
    }
  ];

  const mapDataToExpandedTableRows = (row: any) =>
    row.flyer_runs.map((entry: any) => ({
      flyer_run_name: entry.flyer_run_name ? entry.flyer_run_name : "Undefined",
      spend_to_date: entry.spend ? `$${formatNumber(entry.spend)}` : "$0",
      billable_engagements: entry.billable_engagements
        ? formatNumber(entry.billable_engagements)
        : 0
    }));

  return (
    <Table
      className="expanded-analytics-budget-table"
      size="middle"
      pagination={false}
      columns={columns}
      dataSource={mapDataToExpandedTableRows(entry)}
    />
  );
};

const sortName = (a: any, b: any) => {
  if (a.campaign_name.toLowerCase() > b.campaign_name.toLowerCase()) {
    return 1;
  } else if (a.campaign_name.toLowerCase() < b.campaign_name.toLowerCase()) {
    return -1;
  } else {
    return 0;
  }
};



// WEBPACK FOOTER //
// ./client/modules/AnalyticsMetric/components/Visualizations/AnalyticsBudgetTable/index.tsx